import { PDFArray, PDFDict, PDFName, PDFRawStream, decodePDFRawStream } from 'pdf-lib';
import { ContentStreamParser } from './vendor/libpdf-content-parser';

// Replace only name-token bytes. Re-serializing all operators would round text
// positions or normalize string escapes, changing unrelated page appearances.
export function xObjectInvocations(bytes, colorComponents) {
  const parser = new ContentStreamParser(bytes);
  const tokenizer = parser.tokenizer;
  tokenizer.getScanner().colorComponents = colorComponents;
  const convert = tokenizer.convertToken;
  tokenizer.convertToken = function (token) {
    const result = convert.call(this, token);
    if (result) { result.start = token.position; result.end = this.position; }
    return result;
  };
  const read = tokenizer.reader.readToken;
  tokenizer.reader.readToken = function () {
    const token = read.call(this);
    if (token.type !== 'eof' && this.position <= token.position) throw new Error('Invalid PDF content token');
    return token;
  };
  const { operations, warnings } = parser.parse();
  if (warnings.length) throw new Error('This PDF contains an incomplete content stream. No changes were saved.');
  return operations.filter(op => op.operator === 'Do').map(op => {
    if (op.operands.length !== 1 || op.operands[0].type !== 'name') throw new Error('Invalid PDF drawing reference');
    const operand = op.operands[0];
    // PDF names are arbitrary bytes, not UTF-8 text. Preserve #xx escapes and
    // non-ASCII bytes when resolving the original resource dictionary entry.
    const raw = Array.from(bytes.subarray(operand.start + 1, operand.end), b => String.fromCharCode(b)).join('');
    return { ...operand, value: raw };
  });
}

const join = chunks => {
  const bytes = new Uint8Array(chunks.reduce((length, part) => length + part.length, 0));
  let offset = 0;
  for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.length; }
  return bytes;
};
const newline = new Uint8Array([10]);
const encoder = new TextEncoder();

/**
 * PDFium redaction mutates a Form's underlying stream, including other uses of
 * the same stream. Give every invocation its own stream and resource dictionary
 * before handing the document to the engine. Images are isolated too, so pixel
 * replacement cannot alter another use of the same image.
 */
export function isolatePageDrawings(document, pageNumbers) {
  const ctx = document.context;
  let serial = 0;
  const rewrite = (bytes, inheritedResources, ancestors = new Set()) => {
    const resources = inheritedResources?.clone(ctx) || ctx.obj({});
    const originals = resources.lookupMaybe(PDFName.of('XObject'), PDFDict);
    if (!originals) return { bytes, resources };
    const objects = originals.clone(ctx);
    resources.set(PDFName.of('XObject'), objects);
    const edits = [];
    const used = new Set();
    for (const invocation of xObjectInvocations(bytes, name => {
      const spaces = resources.lookupMaybe(PDFName.of('ColorSpace'), PDFDict);
      const space = spaces?.lookup(PDFName.of(name));
      if (!(space instanceof PDFArray)) return { '/DeviceRGB': 3, '/DeviceGray': 1, '/DeviceCMYK': 4 }[space?.toString()];
      const type = space.get(0).toString();
      if (type === '/ICCBased') return space.lookup(1, PDFRawStream).dict.lookup(PDFName.of('N')).asNumber();
      if (type === '/DeviceN') return space.lookup(1, PDFArray).size();
      return { '/CalRGB': 3, '/CalGray': 1, '/Lab': 3, '/Indexed': 1, '/Separation': 1 }[type];
    })) {
      const key = PDFName.of(invocation.value);
      const original = originals.lookup(key);
      if (!(original instanceof PDFRawStream)) throw new Error('This PDF has an invalid drawing object. No changes were saved.');
      const subtype = original.dict.get(PDFName.of('Subtype'))?.toString();
      if (!['/Form', '/Image'].includes(subtype)) continue;
      if (ancestors.has(original) || ancestors.size > 64 || serial > 100000) {
        throw new Error('This PDF has recursive or excessively complex drawing objects. No changes were saved.');
      }
      const dict = original.dict.clone(ctx);
      let clone;
      if (subtype === '/Form') {
        const ancestry = new Set(ancestors); ancestry.add(original);
        const inner = rewrite(decodePDFRawStream(original).decode(),
          original.dict.lookupMaybe(PDFName.of('Resources'), PDFDict) || inheritedResources, ancestry);
        dict.set(PDFName.of('Resources'), inner.resources);
        dict.delete(PDFName.of('Filter')); dict.delete(PDFName.of('DecodeParms'));
        clone = ctx.flateStream(inner.bytes);
        for (const [name, value] of dict.entries()) if (name !== PDFName.of('Length')) clone.dict.set(name, value);
      } else clone = PDFRawStream.of(dict, original.getContents().slice());
      let name;
      do { name = PDFName.of(`ScrivarIsolated${++serial}`); } while (objects.has(name));
      objects.set(name, ctx.register(clone));
      used.add(key);
      edits.push({ ...invocation, replacement: encoder.encode(name.toString()) });
    }
    // Do not retain obsolete original streams in the edited resource scope.
    for (const key of used) objects.delete(key);
    const chunks = []; let offset = 0;
    for (const edit of edits) {
      chunks.push(bytes.subarray(offset, edit.start), edit.replacement);
      offset = edit.end;
    }
    chunks.push(bytes.subarray(offset));
    return { bytes: join(chunks), resources };
  };
  for (const number of new Set(pageNumbers)) {
    const page = document.getPage(number - 1);
    const contents = page.node.Contents();
    const streams = contents instanceof PDFArray ? contents.asArray() : contents ? [contents] : [];
    // A Contents array is one logical stream: operands and graphics state can
    // span its elements. Parse the concatenated bytes, not each stream alone.
    const bytes = join(streams.flatMap(ref => [decodePDFRawStream(ctx.lookup(ref)).decode(), newline]));
    const result = rewrite(bytes, page.node.Resources());
    page.node.set(PDFName.of('Resources'), result.resources);
    page.node.set(PDFName.of('Contents'), ctx.register(ctx.flateStream(result.bytes)));
  }
}
